import React, { useCallback, useState } from "react";
import { fetchSecurityEvents } from "../../services/api";

/**
 * Administrator export of tenant security_events for compliance review.
 */
const SecurityEventsExport = ({ hideHeader = false }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [eventType, setEventType] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchSecurityEvents({
        event_type: eventType.trim() || undefined,
        limit: 200,
      });
      setEvents(Array.isArray(data?.events) ? data.events : []);
    } catch (e) {
      setError(e.message || "Failed to load security events");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [eventType]);

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify({ events }, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `security-events-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 space-y-3">
      {!hideHeader ? (
        <h3 className="text-lg font-semibold text-slate-900">Security events</h3>
      ) : null}
      <p className="text-sm text-slate-600">
        Exportable audit trail for SSO, admin user changes, SCIM, and agent
        proposal decisions.
      </p>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-sm text-slate-700">
          Event type filter
          <input
            type="text"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            placeholder="e.g. auth.oidc.login"
            className="mt-1 block w-56 border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 text-sm bg-slate-800 text-white rounded"
        >
          {loading ? "Loading…" : "Load"}
        </button>
        <button
          type="button"
          onClick={downloadJson}
          disabled={!events.length}
          className="px-3 py-1.5 text-sm bg-slate-200 text-slate-800 rounded disabled:opacity-50"
        >
          Download JSON
        </button>
      </div>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <p className="text-xs text-slate-500">{events.length} event(s) loaded</p>
      {events.length > 0 ? (
        <pre className="text-xs bg-slate-50 p-3 overflow-auto max-h-96 rounded border border-slate-200">
          {JSON.stringify(events.slice(0, 20), null, 2)}
          {events.length > 20 ? "\n… truncated in preview; download for full set" : ""}
        </pre>
      ) : null}
    </div>
  );
};

export default SecurityEventsExport;
